import { useSearchParams } from 'react-router-dom';

import { BrandTile } from '@/components/BrandTile';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Sheet, Stack, Typography } from '@mui/joy';

// The codes the agents service puts in `error` (McpServerAuthEndpoints, GSB-7801). `mcp_<code>` is the external
// authorization server's OAuth error or the service's own; only `mcp_access_denied` has a reason of its own.
// A Map, not an object literal: `error` comes from the address, and `constructor` must not find a prototype member.
const FAILURE_REASONS = new Map<string, string>([
  ['ticket_invalid', 'Посилання на підключення недійсне.'],
  ['ticket_expired', 'Посилання на підключення застаріло: воно діє 5 хвилин.'],
  ['ticket_used', 'Посилання на підключення вже використано.'],
  [
    'user_mismatch',
    'У Gasbi AI ви увійшли як інший користувач — увійдіть під тим обліковим записом, з якого натиснули «Підключити».',
  ],
  ['server_not_found', 'Сервер не знайдено або його вимкнено в каталозі.'],
  ['mcp_access_denied', 'Доступ на сторінці сервера не надано.'],
]);

const SERVER_REFUSED = 'Сервер відмовив у підключенні.';
const UNKNOWN_FAILURE = 'Сталася непередбачена помилка.';

const ResultTile = ({ failed }: { failed: boolean }) => (
  <Sheet
    variant='soft'
    color={failed ? 'danger' : 'success'}
    sx={{
      width: 80,
      height: 80,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 40,
      mb: 2,
    }}
  >
    {failed ? (
      <CloseIcon fontSize='inherit' />
    ) : (
      <CheckIcon fontSize='inherit' />
    )}
  </Sheet>
);

const Connected = ({ name }: { name?: string }) => (
  <>
    <ResultTile failed={false} />
    <Typography level='h3'>
      {name ? `Сервер ${name} підключено` : 'Сервер підключено'}
    </Typography>
    <Typography level='body-md' textColor='neutral.600'>
      Цю вкладку можна закрити.
    </Typography>
    <Typography level='body-sm' textColor='neutral.500' sx={{ maxWidth: 400 }}>
      Агенти відтепер працюють із цим сервером, а на картці сервера в Admin
      видно, хто його підключив.
    </Typography>
  </>
);

const Failed = ({ name, error }: { name?: string; error: string }) => {
  const knownReason = FAILURE_REASONS.get(error);
  const reason =
    knownReason ??
    (error.startsWith('mcp_') ? SERVER_REFUSED : UNKNOWN_FAILURE);

  return (
    <>
      <ResultTile failed />
      <Typography level='h3'>
        {name
          ? `Не вдалося підключити сервер ${name}`
          : 'Не вдалося підключити сервер'}
      </Typography>
      <Typography
        level='body-md'
        textColor='neutral.600'
        sx={{ maxWidth: 400 }}
      >
        {reason}
      </Typography>
      {!knownReason && error && (
        <Typography level='body-xs' textColor='neutral.500'>
          Код: {error}
        </Typography>
      )}
      <Typography
        level='body-sm'
        textColor='neutral.500'
        sx={{ maxWidth: 400 }}
      >
        Поверніться в Admin і натисніть «Підключити» ще раз.
      </Typography>
    </>
  );
};

/**
 * `/mcp/connected?server=<id>[&name=<name>][&error=<code>]` — the end of connecting an external MCP server in the Admin
 * catalog (ADR-0004 §2.10, PO, 2026-10-12): Admin opened this tab, the agents service ran the server's OAuth and sent
 * the tab here. Outside the session gate: the flow is over, the page only reports it. Admin refreshes the server card
 * when the administrator comes back to its tab, so nothing is posted back. The query values are rendered as text only.
 * A tab the script may not close stays, hence the text that says it can be closed.
 */
export const McpConnectedPage = () => {
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name')?.trim();
  const error = searchParams.get('error');

  return (
    <Stack alignItems='center' sx={{ height: '100dvh', p: 3 }}>
      <Stack direction='row' alignItems='center' gap={1}>
        <BrandTile size={28} />
        <Typography level='title-md'>Gasbi AI</Typography>
      </Stack>
      <Stack
        alignItems='center'
        justifyContent='center'
        gap={1}
        sx={{ flex: 1, textAlign: 'center' }}
      >
        {error === null ? (
          <Connected name={name} />
        ) : (
          <Failed name={name} error={error} />
        )}
        <Button
          variant='outlined'
          color='neutral'
          onClick={() => window.close()}
          sx={{ mt: 2 }}
        >
          Закрити вкладку
        </Button>
      </Stack>
    </Stack>
  );
};
